import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { PLATFORM_DEFAULT_BRAND, type BrandTokens } from '@edv-next/ui';
import { api } from './api.js';

interface BrandCtx {
  brand: BrandTokens & { organization_id: string | null; fallback: boolean };
}
const Ctx = createContext<BrandCtx | null>(null);

const HEX = /^#[0-9a-fA-F]{6}$/;
function sanitizeHex(v: string | null | undefined): string | null {
  return typeof v === 'string' && HEX.test(v) ? v : null;
}
function sanitizeUrl(v: string | null | undefined): string | null {
  if (typeof v !== 'string') return null;
  if (!/^(https?:|storage:\/\/)/i.test(v)) return null;
  if (v.length > 2048) return null;
  return v;
}

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brand, setBrand] = useState<BrandCtx['brand']>(() => ({
    ...PLATFORM_DEFAULT_BRAND,
    organization_id: null,
    fallback: true,
  }));
  useEffect(() => {
    (async () => {
      try {
        const r = await api.publicBranding();
        setBrand({
          organization_id: r.organization_id,
          product_name: String(r.product_name || PLATFORM_DEFAULT_BRAND.product_name).slice(0, 200),
          logo_url: sanitizeUrl(r.logo_url),
          compact_logo_url: sanitizeUrl(r.compact_logo_url),
          favicon_url: sanitizeUrl(r.favicon_url),
          primary_color: sanitizeHex(r.primary_color),
          secondary_color: sanitizeHex(r.secondary_color),
          accent_color: sanitizeHex(r.accent_color),
          preferred_text_theme: r.preferred_text_theme ?? 'auto',
          support_email: r.support_email,
          co_brand_mode: r.co_brand_mode,
          fallback: r.fallback,
        });
      } catch {
        /* leave defaults */
      }
    })();
  }, []);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const r = document.documentElement;
    if (brand.primary_color) r.style.setProperty('--brand-primary', brand.primary_color);
    if (brand.secondary_color) r.style.setProperty('--brand-secondary', brand.secondary_color);
    if (brand.accent_color) r.style.setProperty('--brand-accent', brand.accent_color);
    document.title = brand.product_name;
  }, [brand]);
  const value = useMemo<BrandCtx>(() => ({ brand }), [brand]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBrand(): BrandTokens & { organization_id: string | null; fallback: boolean } {
  const v = useContext(Ctx);
  if (!v) throw new Error('BrandProvider missing');
  return v.brand;
}
